<script>
import Button from '@/components/Button';
import { mapState } from 'vuex';
import Exchange from '@/components/Modals/Exchange';
import { addNotification, clearNotification } from '@/utils/notification';
import { getSignature } from '@/helpers/api/launchpad';

export default {
  name: 'SaleStageButtons',
  components: {
    Button,
    Exchange,
  },
  data() {
    return {
      show: false,
      isLoading: false,
      isApproved: false,
    };
  },
  props: {
    currentStage: {
      type: Number,
      default: 0,
    },
    info: {
      type: Object,
      default: () => {},
    },
    isBinance: {
      type: Boolean,
      default: false,
    },
    hasClaim: {
      type: Boolean,
      default: false,
    },
    lockerId: {
      type: Number,
    },
    saleId: {
      type: String,
    },
  },
  computed: {
    ...mapState({
      ethAddress: (state) => state.User.ethAddress,
    }),
    stakeAllowance() {
      return Number(this.info.stake) < Number(this.info.minStake);
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    updateShow() {
      this.show = !this.show;
      this.$emit('update');
    },
    async init() {
      this.isApproved = await this.checkApprove();
    },
    async registerStake() {
      this.isLoading = true;
      try {
        const { message, signature } = await getSignature(this.ethAddress);
        await this.$contracts
          .getContract('launchpadSale')
          .methods.registerStake(this.saleId, message, signature)
          .send({ from: this.ethAddress })
          .once('transactionHash', (hash) => {
            addNotification(
              {
                type: 'info',
                title: 'Pending register stake:',
                data: { hash },
              },
              { root: true },
            );
          })
          .once('confirmation', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'success',
                title: 'Successfully registered!',
                config: { duration: 15000 },
              },
              { root: true },
            );
            this.$emit('update');
            this.isLoading = false;
          })
          .once('error', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'warn',
                title: 'Register stake failed',
                config: { duration: 5000 },
              },
              { root: true },
            );
            this.isLoading = false;
          });

        return true;
      } catch (e) {
        this.isLoading = false;
        console.error(e);
        return false;
      }
    },
    async claim() {
      this.isLoading = true;
      try {
        await this.$contracts
          .getContract('launchpadSale')
          .methods.claim(this.saleId)
          .send({ from: this.ethAddress })
          .once('transactionHash', (hash) => {
            addNotification(
              {
                type: 'info',
                title: 'Pending claim:',
                data: { hash },
              },
              { root: true },
            );
          })
          .once('confirmation', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'success',
                title: 'Successful claim!',
                config: { duration: 15000 },
              },
              { root: true },
            );
            this.$emit('update');
            this.isLoading = false;
          })
          .once('error', () => {
            clearNotification('info');
            addNotification(
              {
                type: 'warn',
                title: 'Claim failed',
                config: { duration: 5000 },
              },
              { root: true },
            );
            this.isLoading = false;
          });

        return true;
      } catch (e) {
        this.isLoading = false;
        console.error(e);
        return false;
      }
    },
    async getSaleParticipants() {
      try {
        const saleParticipants = await this.$contracts
          .getContract('launchpadSale')
          .methods.getSaleParticipants(this.saleId)
          .call();
        return {
          totalParticipants: Number(saleParticipants.totalParticipants),
          maxParticipant: Number(saleParticipants.maxParticipant),
        };
      } catch (e) {
        return false;
      }
    },
    async checkSaleParticipants() {
      const participants = await this.getSaleParticipants();
      return participants.totalParticipants >= participants.maxParticipant;
    },
    async checkApprove() {
      try {
        // TODO: add min stake amount
        const stakeAmountFinally = this.$contracts.toWei(100 * 10);
        const approvedAmount = await this.$contracts
          .getContract(this.info.paymentToken)
          .methods.allowance(this.ethAddress, this.$contracts.getAddress('launchpadSale'))
          .call();
        return this.$BN(approvedAmount).gte(stakeAmountFinally);
      } catch (e) {
        return false;
      }
    },
    async approve() {
      if (!(await this.checkSaleParticipants() && !(this.info.currentAllocation > 0))) {
        this.isLoading = true;
        if (!(await this.checkApprove())) {
          try {
            await this.$contracts
              .getContract(this.info.paymentToken)
              .methods.approve(
                this.$contracts.getAddress('launchpadSale'),
                this.$BN(2)
                  .pow(256)
                  .minus(1)
                  .toString(),
              )
              .send({ from: this.ethAddress })
              .once('transactionHash', (hash) => {
                addNotification(
                  {
                    type: 'info',
                    title: 'Pending approve:',
                    data: { hash },
                  },
                  { root: true },
                );
              })
              .once('confirmation', () => {
                clearNotification('info');
                addNotification(
                  {
                    type: 'success',
                    title: 'Successfully approved!',
                    config: { duration: 15000 },
                  },
                  { root: true },
                );
                this.$emit('update');
                this.isLoading = false;
                this.isApproved = true;
              })
              .once('error', () => {
                clearNotification('info');
                addNotification(
                  {
                    type: 'warn',
                    title: 'Approve failed',
                    config: { duration: 5000 },
                  },
                  { root: true },
                );
                this.isLoading = false;
              });
          } catch (e) {
            this.isApproved = false;
            this.isLoading = false;
          }
        }

        await this.registerStake();
      }
    },
  },
};
</script>

<template>
  <div>
    <v-row class="mt-10 mb-4" justify="center">
      <div
        class="launchpad-title--zero-stage"
        :class="isBinance ? 'launchpad-title--zero-stage-bsc' : ''"
        v-if="currentStage === 0"
      ></div>
      <div v-if="currentStage === 2">
        <Button
          @click="approve"
          v-if="info.stake !== info.currentAllocation || info.stake === 0"
          :disabled="
            isLoading ||
              (info.isMaxParticipants && !(info.currentAllocation > 0)) ||
              stakeAllowance ||
              info.isMaxStake ||
              !info.isWhiteListed
          "
          :loading="isLoading"
        >
          {{ !isApproved ? 'Participate' : 'Approve' }}
        </Button>
        <span class="registered" v-else>
          Registered
        </span>
      </div>
      <div
        v-if="(currentStage > 3 || currentStage === 0) && !hasClaim && isBinance && info.lockerId"
      >
        <h4 class="launchpad__subline">{{ $t('upcoming.please-wait') }}</h4>
        <div class="text-center">
          <Button
            class="nft-link mt-10"
            :to="{
              name: 'claim',
              params: {
                pid: lockerId,
              },
            }"
          >
            Vesting
          </Button>
        </div>
      </div>
      <Button
        class="nft-link"
        @click="claim"
        v-else-if="currentStage > 3 && hasClaim"
        :disabled="info.isClaimed || isLoading || !(info.currentAllocation > 0)"
        :loading="isLoading"
      >
        Claim
      </Button>
      <Button
        class="nft-link"
        @click="show = true"
        v-else-if="currentStage === 3"
        :disabled="
          isLoading ||
            !(info.currentAllocation > 0) ||
            stakeAllowance ||
            !(Number(info.leftToBuy) > 0)
        "
        :loading="isLoading"
      >
        Buy allocation
      </Button>
    </v-row>
    <Exchange :show="show" @update:show="updateShow" :data="info" :saleId="saleId" />
  </div>
</template>

<style lang="scss"></style>
